// ============================================================================
// apps/AppHost.tsx — ГРАНИЦА ИЗОЛЯЦИИ приложения (SSoT/SRP).
//
// Каждое ПРИЛОЖЕНИЕ (kind='app': чат, Excel, CS, браузер — что угодно)
// рендерится ВНУТРИ AppHost:
//   <AppHost manifest renderer={Component} />
//
// Гарантии:
//   - РЕНДЕР-ОШИБКА приложения → локальный экран «Приложение недоступно»
//     (+ «Перезагрузить») — платформа и ДРУГИЕ приложения/воркспейсы ЖИВЫ
//     (ErrorBoundary, как виджеты рабочих столов).
//   - Жизненный цикл: mount → createAppSdk (подписки событий собраны);
//     unmount → dispose (АВТООТПИСКА всех топиков).
//   - Приложение НЕ имеет прямого доступа к сторам/роутеру — только sdk.*.
//   - Событие app.{code}.error — в журнал (не всплывает в ядро).
// ============================================================================

import { Component, useEffect, useRef, useState, type ErrorInfo } from 'react';
import { createAppSdk, ALL_APP_SCOPES, type AppManifest, type AppSdk } from '@/lib/apps/sdk';
import { createLogger } from '@/lib/logger';
import { Button } from '@/components/ui/button';

const log = createLogger('AppHost');

export interface AppHostProps {
  manifest: AppManifest;
  /** Компонент приложения (единственная точка входа; получает sdk) */
  children: (sdk: AppSdk) => React.ReactNode;
}

export function AppHost({ manifest, children }: AppHostProps) {
  const sdkRef = useRef<{ sdk: AppSdk; dispose: () => void } | null>(null);
  if (!sdkRef.current) {
    // Разрешения: манифест приложения. ПУСТО = доверенное системное приложение
    // (чат и встроенные рендереры) — выдаём ВСЕ скоупы; сторонние приложения
    // получат permissions из метаданных (kind='app' manifest.permissions),
    // когда механизм метаданных-прав появится (см. план: политика выдачи).
    const perms = manifest.permissions?.length ? manifest.permissions : ALL_APP_SCOPES;
    sdkRef.current = createAppSdk({ ...manifest, permissions: perms }, perms);
  }
  const [crashKey, setCrashKey] = useState(0);
  const [crashed, setCrashed] = useState(false);

  useEffect(() => {
    return () => {
      sdkRef.current?.dispose();
      sdkRef.current = null;
    };
  }, []);

  const onCrash = (_err: Error, _info: ErrorInfo) => {
    setCrashed(true);
    // При падении приложения — НЕМЕДЛЕННО снять все подписки (dispose),
    // чтобы упавшее приложение не держало обработчики событий ядра.
    sdkRef.current?.dispose();
    sdkRef.current = null;
    log.error(`app ${manifest.code} crashed`, { error: String(_err?.message || _err), detail: _info?.componentStack || '' });
  };

  // Перезагрузка приложения после краша — новый sdk (чистые подписки)
  const reloadApp = () => {
    setCrashed(false);
    setCrashKey((k) => k + 1);
  };

  if (crashed) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 h-full min-h-40 p-6">
        <div className="text-sm font-medium">Приложение «{manifest.synonym || manifest.name}» недоступно</div>
        <div className="text-xs text-muted-foreground max-w-md text-center">
          Произошла ошибка приложения. Платформа продолжает работать — другие приложения не затронуты.
          Ошибка записана в журнал (категория ui, приложение {manifest.code}).
        </div>
        <Button
          size="sm"
          onClick={reloadApp}
        >
          Перезагрузить приложение
        </Button>
      </div>
    );
  }

  return (
    <AppErrorBoundary key={crashKey} onCrash={onCrash}>
      <div className="h-full w-full overflow-hidden flex flex-col">
        {children(sdkRef.current.sdk)}
      </div>
    </AppErrorBoundary>
  );
}

interface AppErrorBoundaryProps {
  onCrash: (err: Error, info: ErrorInfo) => void;
  children: React.ReactNode;
}

class AppErrorBoundary extends Component<AppErrorBoundaryProps, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onCrash(error, info);
  }

  render() {
    if (this.state.error) {
      // сбрасываем в fallback AppHost (краш-экран выше)
      return null;
    }
    return this.props.children;
  }
}
